/**
 * v3.documents — Retail Document Library (`ao_Documents` SharePoint library).
 *
 * Routes covered:
 *   - GET    /v3/documents/list          — list (cifId? cartId? category? top?)
 *   - GET    /v3/documents/search        — full-text search across the library
 *   - GET    /v3/documents/{id}/content  — stream raw bytes (binary)
 *   - POST   /v3/documents/upload        — multipart-style JSON upload (base64)
 *   - DELETE /v3/documents/{id}          — soft delete (Manager+ on server)
 *   - GET    /v3/documents/types         — DocumentType catalog (docCode list)
 *
 * The `list` route is live (DocumentsFunction.cs); the rest are extension
 * points that the Function exposes alongside it. Each method tolerates the
 * server returning HTTP 503 (`SP_UNAVAILABLE`) and 501 (`NOT_IMPLEMENTED`) and
 * surfaces them as ApiError so the UI can fall back to the fixture path.
 */

import type { RequestOptions } from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

// ---------------------------------------------------------------------------
// Types — must match RdlDocumentRef on the FE side.
// ---------------------------------------------------------------------------

export type DocumentCategory =
  | 'customer_upload'
  | 'fsr_upload'
  | 'signed_pdf'
  | 'rao_output'
  | 'bank_generated';

export type DocumentStatus = 'pending' | 'indexed' | 'exported' | 'failed';

export interface DocumentRef {
  documentId: string;
  fileName: string;
  sizeBytes: number;
  contentType: string;
  category: DocumentCategory;
  auroraStatus: DocumentStatus;
  cifId: string;
  cartId?: string;
  uploadedBy: string;
  uploadedAt: string;
  relativePath: string;
  previewable: boolean;
  /** Optional document-code from DocumentRequirements (DL_FRONT, W9, etc.). */
  docCode?: string;
  // ── AO-DOCUMENTS-SCHEMA-REBUILD (2026-04-25) — v3 ao_Documents columns ──
  /** Plain-language customer name (from ao_Documents.CustomerName index). */
  customerName?: string;
  /** Branch code (e.g. "01"). */
  branch?: string;
  /** Core Director officer code for the FSR who owned the cart/scan. */
  assignedOfficerCode?: string;
  /** v3 DocumentCategory (disclosure / id-scan / uploaded-doc / ...). */
  documentCategory?: string;
  /** Regulated-artifact taxonomy slot (id-scan-front / dl-back / ...). */
  regulatedArtifactKind?: string;
  /** ISO retention end date — when this artifact becomes hard-delete eligible. */
  retentionEndDate?: string;
  /** Test-Mode flag — operational views default to excluding these. */
  isTestMode?: boolean;
  /** Hard-hold flag (litigation, fraud, regulator request). */
  isHardHold?: boolean;
  /** Maint-action linkage when the artifact attaches to a maint flow. */
  maintActionId?: string;
}

export interface DocumentType {
  docCode: string;
  label: string;
  /** Whether this type fulfills a DocumentRequirements slot. */
  fulfillsRequirement: boolean;
  /** Allowed file extensions, e.g. ['pdf','jpg','jpeg','png']. */
  allowedExtensions: string[];
  /** Soft cap, in MB. */
  maxSizeMb?: number;
}

export interface DocumentListResponse {
  success: boolean;
  data: ReadonlyArray<DocumentRef>;
}

export interface DocumentSearchResponse {
  success: boolean;
  data: ReadonlyArray<DocumentRef>;
  /** Server-side hit count (may exceed `data.length` when truncated). */
  totalHits?: number;
}

export interface DocumentTypesResponse {
  success: boolean;
  data: ReadonlyArray<DocumentType>;
}

export interface DocumentContentResponse {
  /**
   * Either `bytesBase64` (small file inline) or `signedUrl` (a URL to GET the
   * bytes from). The Function returns base64 up to a 12 MB inline ceiling;
   * above that it returns `signedUrl`.
   *
   * ⚠ `signedUrl` IS A MISNOMER kept for compatibility with existing callers.
   * It is NOT a storage SAS and carries NO credential of its own — it is a
   * relative path back to this same Function
   * (`/v3/documents/{id}/content?raw=1`) which streams the bytes from
   * SharePoint while staying behind the Function's normal authorization.
   * Fetch it with the client's usual auth headers — `http.getBlob(...)` — not
   * as a bare browser navigation, which would arrive unauthenticated.
   * (These documents live in SharePoint, which has no SAS; Graph's
   * `@microsoft.graph.downloadUrl` was rejected deliberately because it
   * bypasses our authorization for its lifetime.)
   */
  bytesBase64?: string;
  signedUrl?: string;
  contentType: string;
  fileName: string;
  sizeBytes: number;
}

export interface DocumentUploadRequest {
  cifId: string;
  cartId?: string;
  fileName: string;
  contentType: string;
  /** Base-64 encoded file bytes. */
  bytesBase64: string;
  category: DocumentCategory;
  /** Optional DocumentRequirements doc code (e.g. DL_FRONT). */
  docCode?: string;
  /** Optional FSR note for the audit trail. */
  notes?: string;
}

export interface DocumentUploadResponse {
  success: boolean;
  documentId?: string;
  webUrl?: string;
  error?: string;
  errorCode?: string;
}

export interface DocumentDeleteResponse {
  success: boolean;
  error?: string;
  errorCode?: string;
}

export interface DocumentSearchFilters {
  q?: string;
  cifId?: string;
  customerName?: string;
  docCode?: string;
  category?: DocumentCategory;
  /** ISO-8601. */
  from?: string;
  /** ISO-8601. */
  to?: string;
  top?: number;
  // ── AO-DOCUMENTS-SCHEMA-REBUILD (2026-04-25) — indexed-column filters ──
  /** Branch code filter (e.g. "01"). */
  branch?: string;
  /** Core Director officer code filter. */
  officerCode?: string;
  /**
   * Include Test-Mode rows. Defaults to false on the server, so omitting this
   * (or sending false) keeps QA artifacts out of operational views.
   */
  includeTestMode?: boolean;
  /** Filter to a specific maint action's evidence trail. */
  maintActionId?: string;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export class DocumentsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /** List documents — server-side filters applied to ao_Documents. */
  public list(
    filters: { cifId?: string; cartId?: string; category?: DocumentCategory; top?: number } = {},
    opts?: RequestOptions,
  ): Promise<DocumentListResponse> {
    const query: Record<string, string | number | boolean | undefined> = {};
    if (filters.cifId) query.cifId = filters.cifId;
    if (filters.cartId) query.cartId = filters.cartId;
    if (filters.category) query.category = filters.category;
    if (filters.top) query.top = filters.top;
    return this.http.get<DocumentListResponse>('/v3/documents/list', { ...opts, query });
  }

  /** Search across the whole library — Manager+ scope on the server. */
  public search(
    filters: DocumentSearchFilters,
    opts?: RequestOptions,
  ): Promise<DocumentSearchResponse> {
    const query: Record<string, string | number | boolean | undefined> = {};
    if (filters.q) query.q = filters.q;
    if (filters.cifId) query.cifId = filters.cifId;
    if (filters.customerName) query.customerName = filters.customerName;
    if (filters.docCode) query.docCode = filters.docCode;
    if (filters.category) query.category = filters.category;
    if (filters.from) query.from = filters.from;
    if (filters.to) query.to = filters.to;
    if (filters.top) query.top = filters.top;
    if (filters.branch) query.branch = filters.branch;
    if (filters.officerCode) query.officerCode = filters.officerCode;
    if (filters.includeTestMode) query.includeTestMode = true;
    if (filters.maintActionId) query.maintActionId = filters.maintActionId;
    return this.http.get<DocumentSearchResponse>('/v3/documents/search', { ...opts, query });
  }

  /** Fetch raw bytes (or signed URL) for preview / download. */
  public content(documentId: string, opts?: RequestOptions): Promise<DocumentContentResponse> {
    return this.http.get<DocumentContentResponse>(
      `/v3/documents/${encodeURIComponent(documentId)}/content`,
      opts,
    );
  }

  /**
   * Stream a document's raw bytes as a Blob, with full auth headers. Use for
   * anything over the inline ceiling — i.e. whenever `content()` came back with
   * `signedUrl` instead of `bytesBase64`. Nothing is buffered server-side, so
   * this works for a document of any size.
   */
  public contentBlob(documentId: string, opts?: RequestOptions): Promise<Blob> {
    return this.http.getBlob(
      `/v3/documents/${encodeURIComponent(documentId)}/content`,
      { ...opts, query: { raw: '1' } },
    );
  }

  /** Upload a document, base64-encoded — server writes to ao_Documents. */
  public upload(
    req: DocumentUploadRequest,
    opts?: RequestOptions,
  ): Promise<DocumentUploadResponse> {
    return this.http.post<DocumentUploadResponse>('/v3/documents/upload', {
      ...opts,
      body: req,
    });
  }

  /** Soft delete — server marks the item Hidden and writes an audit row. */
  public remove(
    documentId: string,
    reason?: string,
    opts?: RequestOptions,
  ): Promise<DocumentDeleteResponse> {
    const query: Record<string, string | undefined> = {};
    if (reason) query.reason = reason;
    return this.http.del<DocumentDeleteResponse>(
      `/v3/documents/${encodeURIComponent(documentId)}`,
      { ...opts, query },
    );
  }

  /** Fetch the DocumentType catalog (docCode list). */
  public types(opts?: RequestOptions): Promise<DocumentTypesResponse> {
    return this.http.get<DocumentTypesResponse>('/v3/documents/types', opts);
  }

  /**
   * Upload an attached doc captured by the scanner + classifier flow. The
   * response carries the SharePoint item id, sha256 fingerprint, raw Azure
   * DI document type, and confidence so the caller can stamp the matching
   * AttachedDoc audit row.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public uploadAttached(req: unknown, opts?: RequestOptions): Promise<any> {
    return this.http.post('/v3/documents/upload-attached', { ...opts, body: req });
  }
}
